<template>
  <div>
    <!--
      신원 표시. 기본은 '이름 + 탱크코드'(Tank2D/Tank3D)이고,
      'tid + 이름'을 쓰는 화면(Tank3DV2/TankLorry)은 슬롯으로 바꿔 넣는다.
    -->
    <slot name="identity">
      <CBadge color="primary"
              class="font-sm ml-1 mr-1"
              :class="{'font-lg': $store.state.largeFont}">
        <B>{{ tank.name }}</B>
      </CBadge>
      <CBadge :color="signalVariant(signalColor)">{{ tank.tankCode }}</CBadge>
    </slot>

    <CBadge color="secondary" class="ml-1 mr-1" :style="{backgroundColor: oilColors[tank.oilCode], color: '#222222'}">
      {{ oilCodes[tank.oilCode] }}
    </CBadge>

    <CBadge :color="statusVariant" :class="{ 'blink': isBlink }">{{ statusMsg }}</CBadge>

    <CBadge color="secondary" v-if="lastDt" class="ml-1 small">{{ lastDt }}</CBadge>

    <!-- 뒤에 덧붙일 표시(예: 급유차 입출고 시각) -->
    <slot name="append"/>

    <div class="card-header-actions">
      <CRow class="flex-nowrap align-items-center">
        <!-- 단위 배지(TankUnitBadges). 상태 배지와 입출고 버튼 사이에 놓인다 -->
        <CCol v-if="$slots.units" class="pr-1 d-flex align-items-center">
          <slot name="units"/>
        </CCol>
        <CCol v-if="showInout">
          <!-- 입출고 버튼은 아이콘만 표시하고 문구는 툴팁으로 보여준다.
               입고: 탱크로 들어오는 화살표, 출고: 나가는 화살표, 진행 중이면 종료(x) 아이콘 -->
          <CButtonGroup size="sm">
            <CButton class="mr-1 px-2" color="danger" variant="outline"
                     v-b-tooltip.hover :title="sts==='I' ? '입고종료' : '입고시작'"
                     @click="$emit('inout','I')" :disabled="sts==='O'">
              <CIcon :name="sts==='I' ? 'cil-x-circle' : 'cil-arrow-thick-to-bottom'"/>
            </CButton>
            <CButton class="px-2" color="warning" variant="outline"
                     v-b-tooltip.hover :title="sts==='O' ? '출고종료' : '출고시작'"
                     @click="$emit('inout','O')" :disabled="sts==='I'">
              <CIcon :name="sts==='O' ? 'cil-x-circle' : 'cil-arrow-thick-from-bottom'"/>
            </CButton>
          </CButtonGroup>
        </CCol>

        <!-- 버튼 뒤에 붙는 호출측 전용 항목(예: 급유 기종/기번) -->
        <slot name="actions"/>
      </CRow>
    </div>
  </div>
</template>

<style scoped>
/* 상태 뱃지 깜빡임. 호출측 스타일에 기대지 않도록 여기에 둔다 */
@keyframes blink-effect {
  50% {
    opacity: 0;
  }
}

.blink {
  animation: blink-effect 1s step-end infinite;
}
</style>

<script>
/**
 * 탱크 카드 헤더 (신원/유종/상태 뱃지 + 입출고 버튼)
 *
 * Tank2D / Tank3D / Tank3DV2 / TankLorry 의 <BCard slot="header"> 에
 * 같은 블록이 복사돼 있던 것을 하나로 모은 것이다. 유종 뱃지, 상태 뱃지,
 * 입출고 버튼 쌍은 네 곳이 글자까지 동일했다.
 *
 * 상태는 호출측이 그대로 들고 있고 이 컴포넌트는 표시만 한다.
 * 입출고 버튼은 setInout() 을 직접 부르지 않고 @inout 으로 올린다.
 *
 * 컴포넌트마다 달랐던 부분은 props 와 slot 으로 받는다.
 *   - 신원 뱃지 : Tank2D/Tank3D 는 '이름+탱크코드'(기본값),
 *                Tank3DV2/TankLorry 는 'tid+이름' 이라 #identity 로 넘긴다
 *   - 시각 표시 : Tank2D/Tank3D 는 lastDt prop, TankLorry 는 #append 슬롯
 *   - 버튼 노출 : Tank2D/Tank3D 는 탱크용도(tankUse)로 판단하고,
 *                TankLorry 는 쓰지 않는다 (showInout)
 *   - 깜빡임    : 기본은 상태색이 info 가 아닐 때. TankLorry 만 자체 기준이라
 *                statusBlink 로 직접 지정한다
 */
export default {
  name: 'TankCardHeader',
  props: {
    // 탱크 정보 (name, tankCode, tid, oilCode)
    tank: {type: Object, required: true},
    // 카드 테두리와 맞추는 통신상태 색
    signalColor: {type: String, default: 'dark'},
    // 상태 뱃지 색과 문구
    statusVariant: {type: String, default: 'info'},
    statusMsg: {type: String, default: ''},
    // 마지막 수신시각 표시. 빈 값이면 뱃지를 그리지 않는다
    lastDt: {type: String, default: ''},
    // 입출고 정보. 버튼 문구와 활성 여부를 정한다
    inout: {type: Object, default: null},
    // 입출고 버튼 노출 여부
    showInout: {type: Boolean, default: true},
    // 상태 뱃지 깜빡임. 미지정 시 상태색이 info 가 아니면 깜빡인다
    statusBlink: {type: Boolean, default: null},
  },
  data(){
    return {
      oilColors: this.$store.state.codeMaps['OIL_COLOR'],
      oilCodes: this.$store.state.codeMaps['OIL_CODE'],
    }
  },
  computed: {
    // 작업 없음('N')을 기본으로 둔다. 입출고 정보가 아직 없을 때도 버튼이 시작 상태로 보인다.
    sts(){
      return (this.inout && this.inout.sts) || 'N';
    },

    isBlink(){
      return this.statusBlink !== null ? this.statusBlink : this.statusVariant !== 'info';
    },
  },
}
</script>
